import type {
  ListTemplatesResponse,
  RenderTemplateResponse,
  TemplateTargetType,
} from '@compass/client/types/template';
import { useStore } from '@store/store';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { listTemplates, renderTemplate } from '@/services/templates.service';

/**
 * Hook to fetch templates from the current organization.
 *
 * Thin pass-through to listTemplates. Backend filters are strict exact-match
 * (no implicit fallback). Caller controls scope:
 *   - omit programId  → no program filter (returns templates regardless of program_id)
 *   - pass programId  → returns only templates with that exact program_id
 *                       (excludes org-level templates where program_id is null)
 *   - omit workflowId → no workflow filter
 *   - pass workflowId → returns only templates with that exact workflow_id
 *                       (excludes program-level/org-level templates)
 *
 * For workflow → program → org cascade behavior, chain multiple calls in the
 * caller (see KnipperCaseSummaryView).
 */
export const useTemplates = (options?: {
  programId?: string;
  workflowId?: string;
  targetType?: TemplateTargetType;
  slug?: string;
  enabled?: boolean;
}) => {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);

  const context = useMemo(() => {
    if (!selectedOrganizationId) return null;
    return { organizationId: selectedOrganizationId };
  }, [selectedOrganizationId]);

  return useQuery<ListTemplatesResponse>({
    queryKey: [
      'templates',
      selectedOrganizationId,
      options?.programId,
      options?.workflowId,
      options?.targetType,
      options?.slug,
    ],
    queryFn: () =>
      listTemplates(context!, {
        programId: options?.programId,
        workflowId: options?.workflowId,
        targetType: options?.targetType,
        slug: options?.slug,
      }),
    enabled: !!context && options?.enabled !== false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook to render a template with context data (mutation-based for on-demand rendering)
 */
export const useRenderTemplate = () => {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const selectedProgramId = useStore(state => state.selectedProgramId);

  return useMutation<
    RenderTemplateResponse,
    Error,
    { templateId: string; caseRecordId?: string; context?: Record<string, string> }
  >({
    mutationFn: async ({ templateId, caseRecordId, context }) => {
      if (!selectedOrganizationId) {
        throw new Error('Organization ID is required');
      }
      return renderTemplate(
        {
          organizationId: selectedOrganizationId,
          programId: selectedProgramId ?? undefined,
          caseRecordId,
        },
        templateId,
        context,
      );
    },
  });
};
